import { baseApi } from './baseApi';

export type NotificationType =
  | 'auction_request'
  | 'auction_participant'
  | 'auction_bid'
  | 'property_visit'
  | 'mortgage_request'
  | 'wallet_commission'
  | 'wallet_transfer'
  | 'wallet_withdrawal_requested'
  | 'wallet_withdrawal_approved'
  | 'wallet_withdrawal_rejected'
  | 'gustavo_pre_visit'
  | 'gustavo_post_visit'
  | 'bug_report_created'
  | 'bug_report_updated'
  | 'reservation_message'
  | 'property_reserved_rent'
  | 'property_rent_confirmed'
  | 'property_rent_cancelled'
  | 'property_reserved_sale'
  | 'property_sale_confirmed'
  | 'property_sale_cancelled'
  | 'property_published'
  | 'customer_change_request'
  | 'customer_change_resolved'
  | 'customer_activity'
  | 'error_spike'
  | 'error_traffic_drop'
  | 'platform_announcement'
  | 'blog_comment_reply'
  | 'job_application_received'
  | 'referral_application_received'
  | 'referral_reward_earned'
  | 'property_republish_reminder'
  | 'live_session'
  | 'generic';

export interface AppNotification {
  _id:       string;
  userId:    string;
  type:      NotificationType;
  title:     string;
  body:      string;
  link?:     string;
  readAt?:   string;
  meta?:     Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationsResponse {
  data:        AppNotification[];
  unreadCount: number;
}

export interface NotifCategory {
  key:         string;
  label:       string;
  description: string;
  /**
   * Categoría OPT-IN: no llega salvo que el agente la prenda.
   *
   * El backend ya la trataba así, pero la pantalla no la conocía y pintaba el switch
   * encendido —"ausente = ON"—, así que el aviso de captaciones por vencer se veía activo
   * para todo el plantel cuando en realidad no se manda a nadie. (18/08/2026)
   */
  defaultOff?: boolean;
}

export interface NotifPrefsResponse {
  categories: NotifCategory[];
  /** Solo contiene lo que el agente eligió. Ausente = el default de la categoría. */
  prefs:      Record<string, boolean>;
  /** Cada cuántos días quiere el recordatorio de republicar. Default 15. */
  republishReminderDays?: number;
}

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getNotifications: build.query<NotificationsResponse, void>({
      query: () => '/notifications',
      transformResponse: (res: { data: NotificationsResponse }) => res.data,
      providesTags: ['Notification'],
    }),

    markNotificationRead: build.mutation<AppNotification, string>({
      query: (id) => ({ url: `/notifications/${id}/read`, method: 'PUT' }),
      transformResponse: (res: { data: AppNotification }) => res.data,
      invalidatesTags: ['Notification'],
    }),

    markAllNotificationsRead: build.mutation<{ updated: number }, void>({
      query: () => ({ url: '/notifications/read-all', method: 'PUT' }),
      transformResponse: (res: { data: { updated: number } }) => res.data,
      invalidatesTags: ['Notification'],
    }),

    getNotifPrefs: build.query<NotifPrefsResponse, void>({
      query: () => '/notifications/prefs',
      transformResponse: (res: { data: NotifPrefsResponse }) => res.data,
      providesTags: ['NotifPrefs'],
    }),

    updateNotifPrefs: build.mutation<
      NotifPrefsResponse,
      { prefs: Record<string, boolean>; republishReminderDays?: number }
    >({
      query: ({ prefs, republishReminderDays }) => ({
        url: '/notifications/prefs', method: 'PUT',
        body: { prefs, ...(republishReminderDays !== undefined ? { republishReminderDays } : {}) },
      }),
      transformResponse: (res: { data: NotifPrefsResponse }) => res.data,
      invalidatesTags: ['NotifPrefs'],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetNotifPrefsQuery,
  useUpdateNotifPrefsMutation,
} = notificationsApi;
